import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GetAppIcon from '@mui/icons-material/GetApp';
import { Button, Chip, IconButton, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ShortlistPopup } from './ShortlistPopup';

interface CandidateHeaderProps {
  candidateName: string;
  availability: string;
  availabilityDate?: string;
  email: string;
  phone: string;
  location: string;
  matchScore?: number;
  visaStatus?: string;
  payRate?: string;
  source?: string;
  jobTitle?: string;
  showDownload?: boolean;
  showShortlist?: boolean;
  onDownload?: () => void;
  onShortlist?: () => void;
  onShortlistForJob?: () => void;
  onBack?: () => void;
  candidateInternalId?: string;
}

export const CandidateHeader: React.FC<CandidateHeaderProps> = ({
  candidateName,
  availability,
  availabilityDate,
  email,
  phone,
  location,
  matchScore,
  visaStatus,
  payRate,
  source,
  jobTitle,
  showDownload = true,
  showShortlist = true,
  onDownload,
  onShortlist,
  onShortlistForJob,
  onBack,
  candidateInternalId,
}) => {
  const navigate = useNavigate();
  const [popoverOpen, setPopoverOpen] = useState(false);

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  const handleShortlistClick = () => {
    setPopoverOpen(true);
  };

  const handlePopoverClose = () => {
    setPopoverOpen(false);
  };

  return (
    <div
      style={{
        backgroundColor: '#FAFAFB',
        borderBottom: '1px solid #FAFAFB',
        padding: '24px',
      }}>
      {/* Candidate Details Row */}
      <div
        style={{
          alignItems: 'baseline',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}>
        <div
          style={{
            alignItems: 'self-end',
            display: 'flex',
            gap: '2px',
          }}>
          {/* Back Button */}
          <div
            style={{
              alignItems: 'center',
              display: 'flex',
            }}>
            <IconButton
              onClick={handleBack}
              sx={{
                '&:hover': {
                  backgroundColor: '#f3f4f6',
                },
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                marginRight: '12px',
                padding: '8px',
              }}>
              <ArrowBackIcon sx={{ color: '#6b7280', fontSize: '20px' }} />
            </IconButton>
          </div>
          {/* Candidate Name and Info */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}>
            <div style={{ display: 'flex', gap: '12px' }}>
              {/* Candidate Name */}
              <Typography
                sx={{
                  color: '#242424',
                  fontFamily: 'Red Hat Display bold',
                  fontSize: '14px',
                  fontWeight: '600',
                }}>
                {candidateName ? candidateName : '-'}
              </Typography>

              {/* Availability */}
              <div
                style={{
                  alignItems: 'center',
                  display: 'flex',
                }}>
                {availability && (
                  <>
                    <CheckCircleIcon
                      sx={{
                        color: '#0E700E',
                        fontFamily: 'Red Hat Display semibold',
                        fontSize: '12px',
                        fontWeight: '600',
                        marginRight: '6px',
                      }}
                    />
                    <Typography
                      sx={{
                        color: '#0E700E',
                        fontFamily: 'Red Hat Display semibold',
                        fontSize: '12px',
                        fontWeight: '600',
                        marginRight: '6px',
                      }}>
                      {availability}
                    </Typography>
                  </>
                )}
                {availabilityDate && (
                  <>
                    <span
                      style={{
                        backgroundColor: '#0E700E',
                        borderRadius: '100%',
                        height: '4px',
                        marginRight: '6px',
                        width: '4px',
                      }}
                    />
                    <Typography
                      sx={{
                        color: '#0E700E',
                        fontFamily: 'Red Hat Display semibold',
                        fontSize: '12px',
                        fontWeight: '600',
                        marginRight: '6px',
                      }}>
                      {availabilityDate}
                    </Typography>
                  </>
                )}
              </div>
            </div>

            <div
              style={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'space-between',
              }}>
              <Typography
                sx={{
                  display: 'flex',
                  fontFamily: 'Red Hat Display Regular',
                  fontSize: '12px',
                  fontWeight: '400',
                  gap: '4px',
                }}>
                <div
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                    gap: '8px',
                    // marginBottom: isExpanded ? '16px' : '0',
                  }}>
                  <Typography
                    sx={{
                      color: '#616161',
                      fontFamily: 'Red Hat Display Medium',
                      fontSize: '13px',
                      marginBottom: '2px',
                    }}>
                    {email ? email : '-'}
                  </Typography>
                  <span
                    style={{
                      backgroundColor: '#D9D9D9',
                      borderRadius: '100%',
                      height: '4px',
                      width: '4px',
                    }}
                  />
                  <Typography
                    sx={{
                      color: '#616161',
                      fontFamily: 'Red Hat Display Medium',
                      fontSize: '13px',
                      marginBottom: '2px',
                    }}>
                    {phone ? phone : '-'}
                  </Typography>
                  <span
                    style={{
                      backgroundColor: '#D9D9D9',
                      borderRadius: '100%',
                      height: '4px',
                      width: '4px',
                    }}
                  />
                  <Typography
                    sx={{
                      color: '#616161',
                      fontFamily: 'Red Hat Display Medium',
                      fontSize: '13px',
                    }}>
                    {location ? location : '-'}
                  </Typography>
                </div>
              </Typography>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
            gap: '12px',
          }}>
          {showDownload && (
            <IconButton
              onClick={onDownload}
              sx={{
                '&:hover': {
                  backgroundColor: '#f9fafb',
                },
                backgroundColor: '#ffffff',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                padding: '8px',
              }}>
              <GetAppIcon sx={{ color: '#374151', fontSize: '18px' }} />
            </IconButton>
          )}
          {!showShortlist && (
            <Button
              onClick={handleShortlistClick}
              sx={{
                '&:hover': {
                  backgroundColor: '#F3F4F6',
                },
                backgroundColor: '#FFFFFF',
                border: '1px solid #DDD',
                borderRadius: '6px',
                color: '#464646',
                fontFamily: 'Red Hat Display Medium',
                fontSize: '12px',
                fontWeight: 500,
                padding: '8px 16px',
                textTransform: 'none',
              }}>
              Shortlist Candidate
            </Button>
          )}
        </div>
      </div>

      {/* Badges Row */}
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          gap: '8px',
          marginTop: '12px',
          paddingLeft: '47px',
        }}>
        {/* Match Score Badge */}
        {matchScore !== undefined && (
          <Chip
            icon={
              <div
                style={{
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  height: '6px',
                  width: '6px',
                }}
              />
            }
            label={`${matchScore}%`}
            sx={{
              '& .MuiChip-icon': {
                color: 'white',
                marginLeft: '8px',
              },
              backgroundColor: '#EBF0FF',
              border: '1px solid #0262b9',
              color: '#0262b9',
              fontFamily: 'Red Hat Display semibold',
              fontSize: '10px',
              fontWeight: '600',
            }}
          />
        )}
        {/* Bill Rate Badge */}
        {payRate && (
          <Chip
            label={`Bill Rate • ${payRate}`}
            sx={{
              backgroundColor: '#f3f4f6',
              border: '1px solid #f3f4f6',
              color: '#374151',
              fontFamily: 'Red Hat Display Medium',
              fontSize: '10px',
              fontWeight: '600',
            }}
          />
        )}
        {/* Source Badge */}
        {source && (
          <Chip
            label={source}
            sx={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#dc2626',
              fontFamily: 'Red Hat Display Medium',
              fontSize: '10px',
              fontWeight: '600',
              height: '20px',
              textTransform: 'capitalize',
            }}
          />
        )}
        {/* Visa Status Badge */}
        {visaStatus && (
          <Chip
            label={visaStatus}
            sx={{
              backgroundColor: '#EBF0FF',
              border: '1px solid #EBF0FF',
              color: '#0262b9',
              fontFamily: 'Red Hat Display Medium',
              fontSize: '10px',
              fontWeight: '600',
              height: '20px',
            }}
          />
        )}
      </div>

      {/* Shortlist Popup */}
      <ShortlistPopup
        candidateName={candidateName}
        jobTitle={jobTitle}
        onClose={handlePopoverClose}
        onProceed={(selectedItems) => {
          if (onShortlistForJob) {
            onShortlistForJob();
          }
        }}
        open={popoverOpen}
        candidateInternalId={candidateInternalId}
      />
    </div>
  );
};
